import React, { FunctionComponent, ReactNode } from 'react';

import { IReplay } from 'interfaces';
import {Badge, Button, ButtonGroup, Col, Icon, IconButton, Row, Tooltip} from 'cfa-react-components';
import { Gear } from '@cfa-icons/brand';
import { FileService, ReplayConfigService, ReplayService, UserService } from 'services';
import { TaskState, UserRole } from 'enums';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faEye, faRedoAlt, faStop, faTrash } from '@fortawesome/free-solid-svg-icons';

interface IReplayItemProps {
    replay: IReplay,
    openDeleteModal: (target: any, deleteFunc: (any) => Promise<any>, deleteVerb: string, deleteNoun: string) => void,
    openReplayModal: (target: any, repeatFunc: (any) => Promise<any>, repeatNoun: string) => void,
    openMessageModal: (rep: IReplay) => void,
}

export const ReplayItem: FunctionComponent<IReplayItemProps> = ({ replay, openDeleteModal, openReplayModal, openMessageModal }) => {
    
    const config = ReplayConfigService.allReplayConfigsMap[replay.configUUID];

    const configContent: ReactNode = config && (<>
        <Row>
            <h2 className='col'>Configuration Details</h2>
        </Row>
        <Row>
            <Col>{'Name:'}</Col>
            <Col>{config.name}</Col>
        </Row>
        <Row>
            <Col>{'UUID:'}</Col>
            <Col>{config.replayConfigUUID}</Col>
        </Row>
        <Row>
            <Col>{'Sink Type:'}</Col>
            <Col>{config.sinkType}</Col>
        </Row>
        <Row>
            <Col>{'Sink Location:'}</Col>
            <Col>{config.sinkParams.location}</Col>
        </Row>
        <Row>
            <Col>{'Sink Secret Name:'}</Col>
            <Col>{config.sinkParams.secretPath}</Col>
        </Row>
        <Row className='rowLastItem'>
            <Col>{'Timing Option:'}</Col>
            <Col>{config.timingOption}</Col>
        </Row>
    </>);
    
    return (
        <tr key={replay.replayUUID}>
            <td className='itemTable_item'>
                {replay.name}<br/>
                <p style={{fontSize: '12px', margin: '6px 6px 0px'}}>{replay.replayUUID}</p>
            </td>
            <td className='itemTable_item itemTable_itemButton'>
                <Tooltip 
                    content={configContent}
                    placement='right-start'
                    showOnElementEvents={['click']}
                    style={{width: '600px'}}
                >
                    <IconButton size="sm">
                        <Icon icon={Gear} />
                    </IconButton>
                </Tooltip>
            </td>
            <td className='itemTable_item'>
                {replay.startTime > 0 ? new Date(replay.startTime*1000).toLocaleString() : replay.status}
            </td>
            <td className='itemTable_item'>
                {replay.endTime > 0 ? new Date(replay.endTime*1000).toLocaleString() : replay.status}
            </td>
            <td className='itemTable_item'>{replay.captureUUID}</td>
            <td className='itemTable_item itemTable_itemButton'>
                <ButtonGroup size='sm'>
                    {(replay.status == TaskState.Running || replay.status == TaskState.Pending) && config.sinkType == "mqtt" ? (
                        <Tooltip content="View" showOnElementDelay={{showDelay: 1000}}>
                            <Button className='itemTable_iconButton' onClick={() => openMessageModal(replay)}>
                                <FontAwesomeIcon icon={faEye} />
                            </Button>
                        </Tooltip>
                    ) : (
                        <Tooltip content="Repeat" showOnElementDelay={{showDelay: 1000}}>
                            <Button className='itemTable_iconButton' color='secondary'
                                onClick={() => openReplayModal(replay, ReplayService.repeatReplay, 'Replay')} 
                                disabled={replay.status === TaskState.Running || replay.status === TaskState.Pending}
                            >
                                <Badge bordered placement='top-start' content={replay.timesRepeated + 1} hide={replay.timesRepeated == 0}>
                                    <FontAwesomeIcon icon={faRedoAlt} />
                                </Badge>
                            </Button>
                        </Tooltip>
                    )}
                    <Tooltip content="Download" showOnElementDelay={{showDelay: 1000}}>
                        <Button className='itemTable_iconButton'
                            onClick={() => FileService.download({replayUUID: replay.replayUUID})} disabled={config?.sinkType!="csv" || replay.status != TaskState.Success}>
                            <FontAwesomeIcon icon={faDownload} />
                        </Button>
                    </Tooltip>
                    {(replay.status === TaskState.Running || replay.status === TaskState.Pending) ? (
                        <Tooltip content="Cancel" showOnElementDelay={{showDelay: 1000}}>
                            <Button className="itemTable_iconButton" variant="destructive" 
                                onClick={() => openDeleteModal(replay, ReplayService.deleteReplay, 'Cancel', 'Replay')}>
                                <FontAwesomeIcon icon={faStop} />
                            </Button>
                        </Tooltip>
                    ) : (
                        <Tooltip content="Delete" showOnElementDelay={{showDelay: 1000}}>
                            <Button className="itemTable_iconButton" variant="destructive" 
                                onClick={() => openDeleteModal(replay, ReplayService.deleteReplay, 'Delete', 'Replay')}
                                disabled={UserService.oktaUserInfo.role < UserRole.Admin}>
                                <FontAwesomeIcon icon={faTrash} />
                            </Button>
                        </Tooltip>
                    )}
                </ButtonGroup>
            </td>
        </tr>
    );
}
